/**
 * Booking Form Component
 * Create and edit bookings with client/service/staff selection
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

export default function BookingForm({ booking, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    clientId: null,
    serviceId: null,
    staffId: null,
    startTime: '',
    endTime: '',
    notes: ''
  });

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch dropdown data on mount
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Pre-fill form when editing
  useEffect(() => {
    if (booking) {
      const startDate = new Date(booking.start_time);
      const startTimeString = startDate.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm

      setFormData({
        clientId: booking.client_id,
        serviceId: booking.service_id,
        staffId: booking.staff_id,
        startTime: startTimeString,
        endTime: booking.end_time,
        notes: booking.notes || ''
      });

      // Find and set selected service for duration calculation
      const service = services.find(s => s.id === booking.service_id);
      if (service) {
        setSelectedService(service);
      }
    }
  }, [booking, services]);

  // Auto-calculate end time when start time or service changes
  useEffect(() => {
    if (formData.startTime && selectedService) {
      const start = new Date(formData.startTime);
      const end = new Date(start.getTime() + selectedService.duration * 60000);
      setFormData(prev => ({
        ...prev,
        endTime: end.toISOString()
      }));
    }
  }, [formData.startTime, selectedService]);

  const fetchDropdownData = async () => {
    try {
      const [clientsRes, servicesRes, staffRes] = await Promise.all([
        adminAPI.clients.getAll(),
        adminAPI.services.getAll(),
        adminAPI.staff.getAll()
      ]);

      setClients(clientsRes.data.clients || []);
      setServices(servicesRes.data.services || []);
      setStaff(staffRes.data.staff || []);
    } catch (err) {
      console.error('Failed to fetch dropdown data:', err);
      setError('Failed to load form data');
    }
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === parseInt(serviceId));
    setSelectedService(service);
    setFormData(prev => ({
      ...prev,
      serviceId: parseInt(serviceId)
    }));
  };

  const validateForm = () => {
    if (!formData.clientId) return 'Client is required';
    if (!formData.serviceId) return 'Service is required';
    if (!formData.startTime) return 'Date and time are required';

    const startTime = new Date(formData.startTime);
    if (startTime < new Date()) return 'Cannot book in the past';

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        clientId: formData.clientId,
        serviceId: formData.serviceId,
        staffId: formData.staffId || null,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: formData.endTime,
        notes: formData.notes
      };

      if (booking?.id) {
        // Edit mode
        await adminAPI.bookings.update(booking.id, payload);
      } else {
        // Create mode
        await adminAPI.bookings.create(payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save booking');
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const search = clientSearch.toLowerCase();
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  if (!isOpen) return null;

  const today = new Date().toISOString().slice(0, 16);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {booking ? 'Edit Booking' : 'New Booking'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client *
            </label>
            <input
              type="text"
              placeholder="Search clients..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
            />
            <select
              value={formData.clientId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, clientId: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Select a client</option>
              {filteredClients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.first_name} {client.last_name} ({client.email})
                </option>
              ))}
            </select>
          </div>

          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service *
            </label>
            <select
              value={formData.serviceId || ''}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            >
              <option value="">Select a service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name} - {service.duration} min - ${service.price}
                </option>
              ))}
            </select>
          </div>

          {/* Staff Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff (Optional)
            </label>
            <select
              value={formData.staffId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, staffId: e.target.value ? parseInt(e.target.value) : null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Any Available</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>
                  <span
                    style={{ backgroundColor: s.color }}
                    className="inline-block w-3 h-3 rounded-full mr-2"
                  />
                  {s.first_name} {s.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date & Time *
              </label>
              <input
                type="datetime-local"
                value={formData.startTime}
                min={today}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="text"
                value={formData.endTime ? new Date(formData.endTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'Auto-calculated'}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              {selectedService && (
                <p className="text-xs text-gray-500 mt-1">
                  Duration: {selectedService.duration} minutes
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              maxLength={1000}
              placeholder="Add any special notes or requests..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.notes.length}/1000 characters
            </p>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : booking ? 'Update Booking' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
